// Pure helpers for the cat AI — bounds checks, weighted random
// behavior pick, heading math, transition planner. No side effects;
// every function takes the relevant state via arguments. Pulled out
// of cat.ts so the orchestrator can stay focused.

import * as THREE from "three";
import {
  type Behavior,
  type CatPose,
  FADE,
  POSE_IDLE,
  POSE_STAY_BIAS,
  POSE_TRANSITIONS,
  X_MAX,
  X_MIN,
  Z_MAX,
  Z_MIN,
} from "./cat-config";

/**
 * Plan the sequence of pose-transition clips needed to move from `from`
 * to `to`. Returns an empty array when already in the target pose.
 *
 * BFS over the directed graph defined by `POSE_TRANSITIONS`. The pack
 * ships stand↔sit, stand↔lie, and lie↔sleep edges; multi-hop paths
 * (sit→lie via stand; stand→sleep via lie; sit→sleep via stand+lie)
 * fall out automatically. Sleep is *not* directly reachable from stand
 * because the Sleep_to / Sleep_from clips animate within lie-pose
 * height — see POSE_TRANSITIONS comment for the empirical evidence.
 */
export function planTransitions(from: CatPose, to: CatPose): string[] {
  if (from === to) return [];
  type Step = { node: CatPose; clips: string[] };
  const visited = new Set<CatPose>([from]);
  const queue: Step[] = [{ node: from, clips: [] }];
  while (queue.length > 0) {
    const { node, clips } = queue.shift()!;
    for (const next of (
      Object.keys(POSE_TRANSITIONS) as Array<keyof typeof POSE_TRANSITIONS>
    )
      .filter((edge) => edge.startsWith(`${node}->`))
      .map((edge) => edge.slice(edge.indexOf("->") + 2) as CatPose)) {
      if (visited.has(next)) continue;
      visited.add(next);
      const clip =
        POSE_TRANSITIONS[`${node}->${next}` as keyof typeof POSE_TRANSITIONS];
      const nextClips = clip ? [...clips, clip] : clips;
      if (next === to) return nextClips;
      queue.push({ node: next, clips: nextClips });
    }
  }
  return [];
}

/**
 * How long a behavior should run before the AI picks the next one.
 * One-shots run for exactly the clip's authored duration plus a small
 * fade tail (so the crossfade starts after the pose has settled);
 * loops run for a random time in `[durMin, durMax]` so the cat doesn't
 * always switch on exactly the same beat.
 */
export function behaviorDuration(
  b: Behavior,
  action: THREE.AnimationAction,
): number {
  if (!b.loop) {
    return action.getClip().duration + FADE * 0.5;
  }
  const lo = b.durMin ?? 2;
  const hi = b.durMax ?? lo;
  return lo + Math.random() * (hi - lo);
}

// Direct neighbors in the pose graph (built from POSE_TRANSITIONS at
// module load). `pickWeighted` only considers behaviors whose pose is
// in this set, so the AI never plans multi-hop transitions (sit↔lie
// via stand, stand↔sleep via lie, etc.). The cat instead settles into
// one of the intermediate pose's behaviors first — e.g. sit→lie now
// becomes "sit → stand-up → look around or walk → lie down" instead of
// "sit → snap-stand → snap-lie." `triggerBehavior` (debug picker, AI
// supervisor goals) still drives multi-hop via planTransitions when
// asked explicitly.
const POSE_NEIGHBORS: Record<CatPose, Set<CatPose>> = (() => {
  const map = {
    stand: new Set<CatPose>(["stand"]),
    sit: new Set<CatPose>(["sit"]),
    lie: new Set<CatPose>(["lie"]),
    sleep: new Set<CatPose>(["sleep"]),
  };
  for (const edge of Object.keys(POSE_TRANSITIONS) as Array<
    keyof typeof POSE_TRANSITIONS
  >) {
    const arrowIdx = edge.indexOf("->");
    const from = edge.slice(0, arrowIdx) as CatPose;
    const to = edge.slice(arrowIdx + 2) as CatPose;
    map[from].add(to);
  }
  return map;
})();

/**
 * Pick the next behavior with weighted randomness, biased toward
 * staying in the current pose. The just-finished behavior is excluded
 * from the candidate pool so the cat doesn't repeat the same one-shot
 * back-to-back — except for non-stand pose IDLES, which are kept in
 * the pool because excluding them on every re-pick depletes the
 * already-tiny non-stand pools (sit has 4 entries, lie has 2, sleep
 * has 1) and yanks the cat back to stand sooner than feels right.
 *
 * Also restricts candidates to the current pose's direct neighbors in
 * `POSE_TRANSITIONS` (always including the current pose itself) so the
 * AI never schedules a chained transition through an intermediate pose
 * — those briefly snap the cat through the intermediate idle and read
 * as "sit → stand-blip → lie." Multi-hop is still available to manual
 * triggers via `planTransitions`.
 */
export function pickWeighted(
  pool: Behavior[],
  currentPose: CatPose,
  exclude: Behavior | null,
): Behavior {
  let candidates = pool;
  if (exclude) {
    const stickyIdle =
      exclude.loop &&
      exclude.pose !== "stand" &&
      exclude.clip === POSE_IDLE[exclude.pose];
    if (!stickyIdle) {
      candidates = pool.filter((b) => b !== exclude);
    }
  }
  const allowedPoses = POSE_NEIGHBORS[currentPose];
  const reachable = candidates.filter((b) => allowedPoses.has(b.pose));
  const list =
    reachable.length > 0
      ? reachable
      : candidates.length > 0
        ? candidates
        : pool;
  // Bias toward staying in the current pose so we don't churn through
  // pose transitions every other behavior.
  const weights = list.map((b) =>
    b.pose === currentPose ? b.weight * POSE_STAY_BIAS[currentPose] : b.weight,
  );
  let total = 0;
  for (const w of weights) total += w;
  let r = Math.random() * total;
  for (let i = 0; i < list.length; i++) {
    r -= weights[i];
    if (r <= 0) return list[i];
  }
  return list[list.length - 1];
}

/**
 * Pick a heading for the cat to walk along next. Closer to the wall →
 * more bias toward heading-back-to-center; near the middle → more
 * uniform random over (-π, π].
 */
export function pickHeading(pos: THREE.Vector3): number {
  const cx = (X_MIN + X_MAX) / 2;
  const cz = (Z_MIN + Z_MAX) / 2;
  const dx = cx - pos.x;
  const dz = cz - pos.z;
  const distFromCenter = Math.hypot(dx, dz);
  const halfDiag = Math.hypot((X_MAX - X_MIN) / 2, (Z_MAX - Z_MIN) / 2);
  const closeness = Math.min(1, distFromCenter / halfDiag);
  if (Math.random() < 0.4 + closeness * 0.4) {
    const toCenter = Math.atan2(dx, dz);
    return wrapAngle(toCenter + (Math.random() - 0.5) * Math.PI * 0.8);
  }
  return wrapAngle(Math.random() * Math.PI * 2 - Math.PI);
}

/**
 * Heading toward zone center + random jitter. Used at the bound after
 * clamping so the cat steers back into the open area.
 */
export function headingTowardCenter(
  pos: THREE.Vector3,
  jitter: number,
): number {
  const cx = (X_MIN + X_MAX) / 2;
  const cz = (Z_MIN + Z_MAX) / 2;
  const toCenter = Math.atan2(cx - pos.x, cz - pos.z);
  return wrapAngle(toCenter + (Math.random() - 0.5) * jitter);
}

export function outOfBounds(pos: THREE.Vector3): boolean {
  return pos.x < X_MIN || pos.x > X_MAX || pos.z < Z_MIN || pos.z > Z_MAX;
}

export function clampToBounds(pos: THREE.Vector3): void {
  pos.x = Math.max(X_MIN, Math.min(X_MAX, pos.x));
  pos.z = Math.max(Z_MIN, Math.min(Z_MAX, pos.z));
}

/** Normalise an angle to (-π, π]. */
export function wrapAngle(a: number): number {
  let r = a;
  while (r > Math.PI) r -= 2 * Math.PI;
  while (r <= -Math.PI) r += 2 * Math.PI;
  return r;
}
